import React from 'react';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useImageBase } from '../../hooks/useImageBase';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Box,
  Avatar,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import authService from '../../services/authService';
import { useCommonTranslation } from '../../hooks/useCommonTranslation';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import * as uiSlice from '../../data/ui.slice';

const PREFIX = 'HomeAppBar';

const classes = {
  container: `${PREFIX}-container`,
  appName: `${PREFIX}-appName`,
  logo: `${PREFIX}-logo`,
  userInfo: `${PREFIX}-userInfo`,
  toolbar: `${PREFIX}-toolbar`,
  links: `${PREFIX}-links`,
  link: `${PREFIX}-link`,
  selectedLink: `${PREFIX}-selectedLink`,
  icon: `${PREFIX}-icon`,
  root: `${PREFIX}-root`,
  menuButton: `${PREFIX}-menuButton`,
  title: `${PREFIX}-title`,
};

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  [`&.${classes.container}`]: {
    borderRadius: '2px',
  },

  [`& .${classes.appName}`]: {
    marginTop: 'auto',
    marginBottom: 'auto',
    marginRight: theme.spacing(2),
  },

  [`& .${classes.logo}`]: {
    width: 134,
    height: 44,
  },

  [`& .${classes.userInfo}`]: {
    margin: theme.spacing(2),
  },

  [`& .${classes.toolbar}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: '#011012',
    opacity: 0.9,
    color: '#fff',
  },

  [`& .${classes.links}`]: {
    flexGrow: 1,
    display: 'flex',
  },

  [`& .${classes.link}`]: {
    display: 'flex',
    alignItems: 'flex-end',
    textDecoration: 'none',
    color: theme.palette.primary.light,
    padding: theme.spacing(2),
  },

  [`& .${classes.selectedLink}`]: {
    display: 'flex',
    alignItems: 'flex-end',
    textDecoration: 'none',
    color: theme.palette.primary.light,
    fontWeight: 'bold',
    padding: theme.spacing(2),
  },

  [`& .${classes.icon}`]: {
    marginLeft: '10px',
    marginRight: '1px',
  },

  [`& .${classes.root}`]: {
    flexGrow: 1,
  },

  [`& .${classes.menuButton}`]: {
    marginRight: theme.spacing(2),
  },

  [`& .${classes.title}`]: {
    flexGrow: 1,
  },
}));

export default function HomeAppBar() {
  const t = useCommonTranslation();
  const imgBase = useImageBase();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const navMenuCollapsed = useAppSelector(uiSlice.selectNavMenuCollapsed);

  const [userName, setUserName] = React.useState('');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const toggleNavMenu = () => {
    dispatch(uiSlice.toggleNavMenu());
  };

  React.useEffect(() => {
    authService.getUserInfo().subscribe((info) => {
      setUserName(info.name);
    });
  }, []);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const logout = () => {
    closeMenu();
    authService.logout().then(() => navigate('/login'));
  };

  return (
    <StyledAppBar color="transparent" position="sticky">
      <Toolbar className={classes.toolbar}>
        <IconButton
          className={classes.menuButton}
          onClick={toggleNavMenu}
          color="inherit"
          size="large"
          aria-label={
            navMenuCollapsed ? t('app.showMenu') : t('app.hideMenu')
          }>
          <MenuIcon />
        </IconButton>
        <Typography variant="inherit" className={classes.appName}>
          <img
            className={classes.logo}
            src={`${imgBase}/logo2x.png`}
            alt="logo"
          />
        </Typography>
        <Box component="div">
          <IconButton
            onClick={openMenu}
            color="inherit"
            size="large"
            aria-label={t('app.userMenu')}>
            <Avatar component="span" src="/bogus.png" alt={userName}></Avatar>
            <ExpandMore />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={open}
            onClose={closeMenu}>
            <MenuItem onClick={logout}>{t('app.logout')}</MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
}
